// /Frontend/src/pages/AdminUsers.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Server, LogOut, Settings, ArrowLeft, Users,
  Building2, CreditCard, Search, Trash2, Shield,
  ShieldOff, CheckCircle, XCircle, Clock, Filter,
  ChevronDown, X, RefreshCw, UserX, Mail
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AdminNav from '../components/AdminNav';

// ─── Badges ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:    { color: 'bg-green-500/10 text-green-400 border-green-500/20',    label: 'Active' },
    pending:   { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Pending' },
    rejected:  { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Rejected' },
    suspended: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',    label: 'Suspended' },
  };
  const s = map[status] || map.active;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>{s.label}</span>;
};

const RoleBadge = ({ role }) => {
  const map = {
    admin:         { color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',       label: 'Super Admin' },
    company_admin: { color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', label: 'Company Admin' },
    user:          { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',    label: 'User' },
  };
  const s = map[role] || map.user;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>{s.label}</span>;
};

// ─── Main component ───────────────────────────────────────────────────────────
const AdminUsers = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers]           = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [actionId, setActionId]     = useState(null);
  const [stats, setStats]           = useState({ total: 0, active: 0, pending: 0, rejected: 0 });

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, companiesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/companies'),
      ]);

      // /admin/users returns array directly (from User.getAll)
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.users || []);
      setUsers(usersData);
      setCompanies(companiesRes.data.companies || []);

      // Compute stats
      setStats({
        total:    usersData.length,
        active:   usersData.filter(u => u.status === 'active').length,
        pending:  usersData.filter(u => u.status === 'pending').length,
        rejected: usersData.filter(u => u.status === 'rejected').length,
      });
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return users.filter(u => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.company?.toLowerCase().includes(q);
      const matchRole    = filterRole    === 'all' || u.role    === filterRole;
      const matchStatus  = filterStatus  === 'all' || u.status  === filterStatus;
      const matchCompany = filterCompany === 'all' || u.company_id === filterCompany;
      return matchSearch && matchRole && matchStatus && matchCompany;
    });
  }, [users, search, filterRole, filterStatus, filterCompany]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete "${u.first_name} ${u.last_name}" (${u.email})? This cannot be undone.`)) return;
    setDeletingId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      setStats(prev => ({
        ...prev,
        total:   prev.total - 1,
        [u.status]: Math.max(0, (prev[u.status] || 0) - 1),
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePromote = async (userId, companyId) => {
    setActionId(userId);
    try {
      await api.post(`/admin/companies/users/${userId}/promote`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'company_admin' } : u));
    } catch (err) {
      console.error('Error promoting user:', err);
    } finally {
      setActionId(null);
    }
  };

  const handleDemote = async (userId) => {
    setActionId(userId);
    try {
      await api.post(`/admin/companies/users/${userId}/demote`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'user' } : u));
    } catch (err) {
      console.error('Error demoting user:', err);
    } finally {
      setActionId(null);
    }
  };

  const handleApprove = async (userId) => {
    setActionId(userId);
    try {
      await api.post(`/admin/companies/users/${userId}/approve`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'active' } : u));
      setStats(prev => ({ ...prev, active: prev.active + 1, pending: Math.max(0, prev.pending - 1) }));
    } catch (err) {
      console.error('Error approving user:', err);
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this user? They will not be able to log in.')) return;
    setActionId(userId);
    try {
      await api.post(`/admin/companies/users/${userId}/reject`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
      setStats(prev => ({ ...prev, rejected: prev.rejected + 1, pending: Math.max(0, prev.pending - 1) }));
    } catch (err) {
      console.error('Error rejecting user:', err);
    } finally {
      setActionId(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterRole('all');
    setFilterStatus('all');
    setFilterCompany('all');
  };

  const hasActiveFilters = search || filterRole !== 'all' || filterStatus !== 'all' || filterCompany !== 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <AdminNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Page title ── */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold">User Management</h2>
            <p className="text-slate-400 text-sm mt-1">Manage all users across all companies</p>
          </div>
          <button onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors text-sm">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* ── Stats row ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Users',   value: stats.total,    color: 'bg-blue-500/10',   icon: Users,      iconColor: 'text-blue-400',   onClick: () => setFilterStatus('all') },
            { label: 'Active',        value: stats.active,   color: 'bg-green-500/10',  icon: CheckCircle, iconColor: 'text-green-400',  onClick: () => setFilterStatus('active') },
            { label: 'Pending',       value: stats.pending,  color: 'bg-yellow-500/10', icon: Clock,       iconColor: 'text-yellow-400', onClick: () => setFilterStatus('pending') },
            { label: 'Rejected',      value: stats.rejected, color: 'bg-red-500/10',    icon: UserX,       iconColor: 'text-red-400',    onClick: () => setFilterStatus('rejected') },
          ].map(stat => {
            const Icon = stat.icon;
            return (
              <button key={stat.label} onClick={stat.onClick}
                className="bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-xl p-5 text-left transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </div>
                  <span className="text-2xl font-bold">{stat.value}</span>
                </div>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </button>
            );
          })}
        </div>

        {/* ── Filters ── */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email, company..."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Role filter */}
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors">
              <option value="all">All Roles</option>
              <option value="admin">Super Admin</option>
              <option value="company_admin">Company Admin</option>
              <option value="user">User</option>
            </select>

            {/* Status filter */}
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>

            {/* Company filter */}
            <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors">
              <option value="all">All Companies</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors">
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}

            <span className="text-slate-500 text-sm ml-auto">
              {filtered.length} of {users.length} users
            </span>
          </div>
        </div>

        {/* ── Users table ── */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400 mb-1">No users found</p>
              <p className="text-slate-500 text-sm">
                {hasActiveFilters ? 'Try adjusting your filters' : 'No users registered yet'}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left bg-slate-900/50">
                    <th className="px-6 py-3 text-slate-400 font-medium">User</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Company</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Role</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Status</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Joined</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Last Login</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => {
                    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown';
                    const isLoading = actionId === u.id || deletingId === u.id;
                    const isSelf    = u.id === user?.id;
                    const isSuperAdmin = u.role === 'admin';

                    return (
                      <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">

                        {/* User */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500/30 to-cyan-400/30 rounded-full flex items-center justify-center flex-shrink-0 border border-slate-700">
                              <span className="text-xs font-semibold text-blue-300">
                                {(u.first_name?.[0] || '?').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white flex items-center gap-1.5">
                                {fullName}
                                {isSelf && <span className="text-xs text-slate-500">(you)</span>}
                              </p>
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                <Mail className="w-3 h-3" />{u.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Company */}
                        <td className="px-6 py-4">
                          {u.company
                            ? <span className="text-slate-300 text-xs">{u.company}</span>
                            : <span className="text-slate-600 text-xs">—</span>
                          }
                        </td>

                        {/* Role */}
                        <td className="px-6 py-4"><RoleBadge role={u.role} /></td>

                        {/* Status */}
                        <td className="px-6 py-4"><StatusBadge status={u.status} /></td>

                        {/* Joined */}
                        <td className="px-6 py-4 text-slate-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>

                        {/* Last login */}
                        <td className="px-6 py-4 text-slate-400 text-xs">
                          {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">

                            {/* Approve pending */}
                            {u.status === 'pending' && (
                              <button onClick={() => handleApprove(u.id)} disabled={isLoading}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50">
                                <CheckCircle className="w-3 h-3" />Approve
                              </button>
                            )}

                            {/* Reject pending */}
                            {u.status === 'pending' && (
                              <button onClick={() => handleReject(u.id)} disabled={isLoading}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50">
                                <XCircle className="w-3 h-3" />Reject
                              </button>
                            )}

                            {/* Promote to company admin */}
                            {u.role === 'user' && u.status === 'active' && (
                              <button onClick={() => handlePromote(u.id, u.company_id)} disabled={isLoading}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 rounded transition-colors disabled:opacity-50">
                                <Shield className="w-3 h-3" />Promote
                              </button>
                            )}

                            {/* Demote company admin */}
                            {u.role === 'company_admin' && (
                              <button onClick={() => handleDemote(u.id)} disabled={isLoading}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 rounded transition-colors disabled:opacity-50">
                                <ShieldOff className="w-3 h-3" />Demote
                              </button>
                            )}

                            {/* Delete — not for self or other super admins */}
                            {!isSelf && !isSuperAdmin && (
                              <button onClick={() => handleDelete(u)} disabled={isLoading}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-slate-400 border border-slate-600 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 rounded transition-colors disabled:opacity-50">
                                {deletingId === u.id
                                  ? <div className="w-3 h-3 border border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                                  : <Trash2 className="w-3 h-3" />
                                }
                                Delete
                              </button>
                            )}

                            {/* Spinner for other actions */}
                            {actionId === u.id && (
                              <div className="w-4 h-4 border-2 border-slate-600 border-t-slate-400 rounded-full animate-spin" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;